import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DeliveryDashboard.css';
import {
    Package, MapPin, DollarSign, Clock,
    TrendingUp, CheckCircle, AlertCircle,
    Navigation, Phone, Star, LogOut, User, Loader, MapPinOff, X, Eye, Bell
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { toast } from 'react-toastify';

const API_BASE_URL = 'https://api.rismicmart.com';

const DeliveryDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // ==================== STATE DECLARATIONS ====================

    const [stats, setStats] = useState({
        todayDeliveries: 0,
        totalEarnings: 0,
        pendingOrders: 0,
        completedToday: 0,
        averageRating: 0,
        onlineHours: 0
    });

    const [activeOrders, setActiveOrders] = useState([]);
    const [recentDeliveries, setRecentDeliveries] = useState([]);

    // ✅ FIX: Initialize from user.status instead of localStorage
    const [isOnline, setIsOnline] = useState(() => {
        // Always start with the actual database status
        return user?.status === "Available";
    });

    const [loading, setLoading] = useState(false);
    const [fetchingOrders, setFetchingOrders] = useState(true);

    const [currentLocation, setCurrentLocation] = useState(null);
    const [currentAddress, setCurrentAddress] = useState('');
    const [locationError, setLocationError] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [watchId, setWatchId] = useState(null);
    const [locationEnabled, setLocationEnabled] = useState(() => {
        const savedGPSStatus = localStorage.getItem('gpsEnabled');
        return savedGPSStatus === 'true';
    });

    const socketRef = useRef(null);
    const [socketConnected, setSocketConnected] = useState(false);

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);

    const [notificationCount, setNotificationCount] = useState(0);
    const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
    const [pendingNotifications, setPendingNotifications] = useState([]);
    const notificationRef = useRef(null);

    // ==================== AXIOS INSTANCE ====================

    const axiosInstance = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
        }
    });

    axiosInstance.interceptors.request.use((config) => {
        const token = localStorage.getItem('deliveryToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });


    // CONFIRM POPUP
    const [confirmPopup, setConfirmPopup] = useState({
        show: false,
        title: "",
        message: "",
        onConfirm: null
    });
    const openConfirmPopup = (title, message, onConfirm) => {
        setConfirmPopup({ show: true, title, message, onConfirm });
    };

    const closeConfirmPopup = () => {
        setConfirmPopup({ show: false, title: "", message: "", onConfirm: null });
    };

    const [messagePopup, setMessagePopup] = useState({
        show: false,
        title: "",
        message: "",
        type: "info"
    });

    const openMessagePopup = (title, message, type = "info") => {
        setMessagePopup({ show: true, title, message, type });
        setTimeout(() => {
            setMessagePopup({ show: false, title: "", message: "", type: "info" });
        }, 2000);
    };

    const closeMessagePopup = () => {
        setMessagePopup({ show: false, title: "", message: "", type: "info" });
    };

    // ✅ FIX: Sync frontend status with database on mount
    useEffect(() => {
        const syncStatusWithDatabase = async () => {
            if (!user?._id) return;
            
            try {
                // Fetch current user status from database
                const response = await axiosInstance.get(`/api/delivery-person/${user._id}`);
                
                if (response.data.success) {
                    const dbStatus = response.data.data.status;
                    const isAvailable = dbStatus === "Available";
                    
                    // Update frontend state to match database
                    setIsOnline(isAvailable);
                    localStorage.setItem('userStatus', dbStatus);
                    
                    console.log(`✅ Status synced with database: ${dbStatus}`);
                }
            } catch (error) {
                console.error('Error syncing status with database:', error);
            }
        };

        syncStatusWithDatabase();
    }, [user?._id]);

    // ==================== NOTIFICATION FUNCTIONS ====================

    const updateNotificationCount = useCallback(() => {
        const newOrders = activeOrders.filter(order => order.status === 'confirmed');
        setNotificationCount(newOrders.length);
        setPendingNotifications(newOrders);
    }, [activeOrders]);

    useEffect(() => {
        updateNotificationCount();
    }, [activeOrders, updateNotificationCount]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotificationDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // ==================== GEOCODING FUNCTIONS ====================

    const reverseGeocode = async (lat, lng) => {
        try {
            if (window.google && window.google.maps) {
                const geocoder = new window.google.maps.Geocoder();
                const latlng = { lat, lng };

                return new Promise((resolve, reject) => {
                    geocoder.geocode({ location: latlng }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            resolve(results[0].formatted_address);
                        } else {
                            reject('Unable to find address');
                        }
                    });
                });
            } else {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
                );
                const data = await response.json();

                if (data && data.display_name) {
                    return data.display_name;
                } else {
                    throw new Error('Unable to find address');
                }
            }
        } catch (error) {
            console.error('❌ Reverse geocoding failed:', error);
            return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        }
    };

    // ==================== INITIALIZATION EFFECT ====================

    useEffect(() => {
        if (locationEnabled && !currentLocation && !locationLoading) {
            console.log('🔄 Auto-starting GPS from saved state...');
            startLocationTracking();
        }
    }, []);

    // ==================== SOCKET.IO FUNCTIONS ====================

    const connectSocket = useCallback(() => {
        if (socketRef.current?.connected) {
            console.log('✅ Socket already connected');
            return;
        }

        console.log('🔌 Connecting delivery person to Socket.IO...');

        socketRef.current = io(API_BASE_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
            auth: {
                deliveryPersonId: user?._id,
                role: 'delivery'
            }
        });

        socketRef.current.on('connect', () => {
            console.log('✅ Delivery Socket.IO connected:', socketRef.current.id);
            setSocketConnected(true);

            activeOrders.forEach(order => {
                socketRef.current.emit('delivery:register', {
                    orderId: order._id,
                    deliveryPersonId: user._id
                });
            });
        });

        socketRef.current.on('connect_error', (error) => {
            console.error('❌ Socket connection error:', error);
            setSocketConnected(false);
        });

        socketRef.current.on('disconnect', (reason) => {
            console.log('🔌 Socket disconnected:', reason);
            setSocketConnected(false);
        });

        socketRef.current.on('delivery:new-order', (data) => {
            console.log('🔔 New order assigned:', data);
            fetchActiveOrders();
            setNotificationCount(prev => prev + 1);
        });

        socketRef.current.on('order:rejected', (data) => {
            console.log('📡 Order rejection event received:', data);
            setActiveOrders(prev =>
                prev.filter(order => order._id !== data.orderId)
            );
        });

    }, [user?._id, activeOrders]);

    const disconnectSocket = useCallback(() => {
        if (socketRef.current) {
            console.log('🔌 Disconnecting delivery socket...');
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocketConnected(false);
        }
    }, []);

    // ==================== LOCATION TRACKING FUNCTIONS ====================

    const emitLocationUpdate = useCallback((location) => {
        if (!socketRef.current?.connected || !location) {
            return;
        }

        activeOrders.forEach(order => {
            const locationData = {
                orderId: order._id,
                deliveryPersonId: user._id,
                location: {
                    latitude: location.lat,
                    longitude: location.lng,
                    accuracy: location.accuracy || 10
                },
                timestamp: new Date().toISOString(),
                deliveryPerson: {
                    fullName: user.fullName,
                    phone: user.phone,
                    vehicleType: user.vehicleType,
                    vehicleNumber: user.vehicleNumber,
                    profileImage: user.profileImage,
                    ratings: user.ratings
                }
            };

            console.log('📍 Emitting location update via Socket.IO:', locationData);
            socketRef.current.emit('delivery:location-update', locationData);
        });

        updateLocationInDatabase(location);

    }, [activeOrders, user]);

    const updateLocationInDatabase = async (location) => {
        try {
            await axiosInstance.put(`/api/delivery-person/${user._id}/location`, {
                latitude: location.lat,
                longitude: location.lng,
                accuracy: location.accuracy || 10,
                timestamp: new Date()
            });
            console.log('✅ Location saved to database');
        } catch (error) {
            console.error('❌ Failed to save location to database:', error);
        }
    };

    const getLocationErrorMessage = (error) => {
        switch (error.code) {
            case error.PERMISSION_DENIED:
                return 'Location permission denied. Please enable location access in your browser settings.';
            case error.POSITION_UNAVAILABLE:
                return 'Location information unavailable. Please check your GPS/WiFi settings.';
            case error.TIMEOUT:
                return 'Location request timed out. Retrying with optimized settings...';
            default:
                return 'An unknown error occurred while getting location.';
        }
    };

    const startLocationTracking = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser');
            return;
        }

        setLocationLoading(true);
        setLocationError(null);

        const highAccuracyOptions = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 10000
        };

        const lowAccuracyOptions = {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 30000
        };

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp
                };
                setCurrentLocation(location);
                setLocationLoading(false);
                setLocationEnabled(true);
                setLocationError(null);
                localStorage.setItem('gpsEnabled', 'true');
                console.log('📍 High accuracy location acquired:', location);

                const address = await reverseGeocode(location.lat, location.lng);
                setCurrentAddress(address);
                console.log('📍 Address:', address);

                emitLocationUpdate(location);

                startWatchPosition(highAccuracyOptions);
            },
            (error) => {
                console.warn('⚠️ High accuracy failed, trying low accuracy...', error);

                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const location = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            timestamp: position.timestamp
                        };
                        setCurrentLocation(location);
                        setLocationLoading(false);
                        setLocationEnabled(true);
                        setLocationError(null);
                        localStorage.setItem('gpsEnabled', 'true');
                        console.log('📍 Low accuracy location acquired:', location);

                        const address = await reverseGeocode(location.lat, location.lng);
                        setCurrentAddress(address);
                        console.log('📍 Address:', address);

                        emitLocationUpdate(location);

                        startWatchPosition(lowAccuracyOptions);
                    },
                    (fallbackError) => {
                        console.error('❌ Both location strategies failed:', fallbackError);
                        setLocationError(getLocationErrorMessage(fallbackError));
                        setLocationLoading(false);
                        setLocationEnabled(false);
                        localStorage.setItem('gpsEnabled', 'false');
                    },
                    lowAccuracyOptions
                );
            },
            highAccuracyOptions
        );
    };

    const startWatchPosition = (options) => {
        const id = navigator.geolocation.watchPosition(
            async (position) => {
                const location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp
                };
                setCurrentLocation(location);
                setLocationError(null);
                console.log('📍 Location updated:', location);

                const address = await reverseGeocode(location.lat, location.lng);
                setCurrentAddress(address);

                emitLocationUpdate(location);
            },
            (error) => {
                console.error('❌ Location watch error:', error);
                if (error.code === 3) {
                    console.warn('⚠️ Watch position timeout, will retry automatically...');
                } else {
                    setLocationError(getLocationErrorMessage(error));
                }
            },
            options
        );

        setWatchId(id);
        console.log('👀 Started watching position with ID:', id);
    };

    const stopLocationTracking = () => {
        if (watchId) {
            navigator.geolocation.clearWatch(watchId);
            setWatchId(null);
            console.log('🛑 Location tracking stopped');
        }
        setCurrentLocation(null);
        setCurrentAddress('');
        setLocationEnabled(false);
        setLocationError(null);
        localStorage.setItem('gpsEnabled', 'false');
    };

    const handleLocationToggle = () => {
        if (locationEnabled) {
            stopLocationTracking();
        } else {
            startLocationTracking();
        }
    };

    const retryLocation = () => {
        setLocationError(null);
        startLocationTracking();
    };

    useEffect(() => {
        return () => {
            if (watchId) {
                navigator.geolocation.clearWatch(watchId);
            }
        };
    }, [watchId]);

    useEffect(() => {
        if (isOnline && user?._id) {
            connectSocket();
        } else {
            disconnectSocket();
        }
        return () => {
            disconnectSocket();
        };
    }, [isOnline, user?._id, connectSocket, disconnectSocket]);

    useEffect(() => {
        if (socketConnected && activeOrders.length > 0) {
            activeOrders.forEach(order => {
                socketRef.current?.emit('delivery:register', {
                    orderId: order._id,
                    deliveryPersonId: user._id
                });
            });
        }
    }, [socketConnected, activeOrders, user?._id]);

    // ==================== FETCH STATS ====================

    const fetchDeliveryStats = useCallback(async () => {
        if (!user?._id) {
            console.log('❌ No user ID found');
            return;
        }

        try {
            console.log('📊 Fetching delivery stats for:', user._id);

            const response = await axiosInstance.get(
                `/api/orders/delivery-person/${user._id}/stats`
            );

            if (response.data.success) {
                const statsData = response.data.data;

                console.log('========================================');
                console.log('✅ STATS FETCHED SUCCESSFULLY');
                console.log('Average Rating:', statsData.averageRating);
                console.log('Total Ratings:', statsData.totalRatings);
                console.log('Total Deliveries:', statsData.totalDeliveries);
                console.log('========================================');

                setStats(prev => ({
                    ...prev,
                    todayDeliveries: statsData.totalDeliveries,
                    totalEarnings: statsData.totalEarnings,
                    averageRating: statsData.averageRating,
                    completedToday: statsData.completedToday,
                    pendingOrders: statsData.pendingOrders,
                    onlineHours: calculateOnlineHours()
                }));
            }
        } catch (error) {
            console.error('❌ Error fetching delivery stats:', error);
        }
    }, [user?._id]);

    // ==================== FETCH ORDERS FUNCTIONS ====================

    const fetchActiveOrders = useCallback(async () => {
        if (!user?._id) {
            console.log('❌ No user ID found');
            return;
        }

        try {
            setFetchingOrders(true);
            console.log('📦 Fetching orders for delivery person:', user._id);

            const response = await axiosInstance.get(
                `/api/orders/delivery-person/${user._id}`
            );

            console.log('📦 Fetched orders response:', response.data);

            if (response.data.success) {
                const orders = response.data.data || [];
                const active = orders.filter(
                    order => ['confirmed', 'processing', 'shipped'].includes(order.status)
                );

                setActiveOrders(active);
                fetchDeliveryStats();
            }
        } catch (error) {
            console.error('❌ Error fetching active orders:', error);
            if (error.response?.status === 401) {
                logout();
                navigate('/login');
            }
        } finally {
            setFetchingOrders(false);
        }
    }, [user?._id, logout, navigate, fetchDeliveryStats]);

    const fetchRecentDeliveries = useCallback(async () => {
        if (!user?._id) return;

        try {
            const response = await axiosInstance.get(
                `/api/orders/delivery-person/${user._id}`
            );

            if (response.data.success) {
                const allOrders = response.data.data || [];
                const completed = allOrders
                    .filter(order => order.status === 'delivered')
                    .sort((a, b) => new Date(b.deliveredAt || b.updatedAt) - new Date(a.deliveredAt || a.updatedAt))
                    .slice(0, 4);

                setRecentDeliveries(completed);
            }
        } catch (error) {
            console.error('Error fetching recent deliveries:', error);
        }
    }, [user?._id]);

    const calculateOnlineHours = () => {
        return Math.floor(Math.random() * 8) + 1;
    };

    // ==================== MAIN USEEFFECTS ====================

    useEffect(() => {
        if (user?._id) {
            fetchActiveOrders();
            fetchDeliveryStats();
            fetchRecentDeliveries();
        } else {
            navigate('/login');
        }
    }, [user?._id, fetchActiveOrders, fetchDeliveryStats, fetchRecentDeliveries, navigate]);

    useEffect(() => {
        let interval;
        if (isOnline && user?._id) {
            interval = setInterval(() => {
                fetchActiveOrders();
            }, 30000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isOnline, user?._id, fetchActiveOrders]);

    // ==================== STATUS FUNCTIONS ====================

    // ✅ UPDATED: Status toggle handler with proper database sync
    const handleStatusToggle = async () => {
        if (loading) return;

        setLoading(true);

        try {
            const newStatus = isOnline ? "Offline" : "Available";

            const response = await axiosInstance.put(
                `/api/delivery-person/${user._id}`,
                { status: newStatus }
            );

            if (response.data) {
                setIsOnline(!isOnline);
                localStorage.setItem('userStatus', newStatus);
                
                console.log(`✅ Status updated to: ${newStatus}`);
                
                toast.success(`Status changed to ${newStatus}`, {
                    position: "bottom-right",
                    autoClose: 3000
                });

                if (newStatus === "Available") {
                    fetchActiveOrders();
                }
            }
        } catch (error) {
            console.error('❌ Error updating status:', error);
            toast.error('Failed to update status. Please try again.', {
                position: "bottom-right",
                autoClose: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            stopLocationTracking();
            disconnectSocket();
            logout();
            navigate('/', { replace: true });
        }
    };

    const handleViewProfile = () => {
        navigate('/profile');
    };

    // ==================== ORDER ACTION FUNCTIONS ====================

    const handleAcceptOrderFromNotification = async (orderId) => {
        let loadingToastId;
        try {
            loadingToastId = toast.loading('Accepting order...', {
                position: 'bottom-right'
            });

            const response = await axiosInstance.put(
                `/api/orders/${orderId}`,
                {
                    status: 'shipped',
                    deliveryPersonId: user._id
                },
                { timeout: 10000 }
            );

            if (response.data) {
                setActiveOrders(prevOrders =>
                    prevOrders.map(order =>
                        order._id === orderId
                            ? { ...order, status: 'shipped' }
                            : order
                    )
                );

                setStats(prev => ({
                    ...prev,
                    pendingOrders: Math.max(0, prev.pendingOrders - 1)
                }));

                if (socketRef.current?.connected) {
                    socketRef.current.emit('order:status-update', {
                        orderId,
                        status: 'shipped',
                        deliveryPersonId: user._id
                    });
                }

                setPendingNotifications(prev => prev.filter(order => order._id !== orderId));
                setNotificationCount(prev => Math.max(0, prev - 1));

                toast.dismiss(loadingToastId);
                toast.success('Order accepted successfully! 📦', {
                    position: 'bottom-right',
                    autoClose: 3000
                });
            }
        } catch (error) {
            toast.dismiss(loadingToastId);
            console.error('❌ Error accepting order:', error);

            let errorMessage = 'Failed to accept order. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            toast.error(errorMessage, {
                position: 'bottom-right',
                autoClose: 4000
            });
        }
    };

    const handleRejectOrderFromNotification = async (orderId) => {
        openConfirmPopup(
            "Reject Order?",
            "Are you sure you want to reject this order?\nThe order will be returned to pending and available for other delivery agents.",
            () => handleActualRejection(orderId)
        );
    };

    const handleActualRejection = async (orderId) => {
        let loadingMessageShown = false;

        try {
            openMessagePopup("Processing...", "Rejecting order, please wait...");
            loadingMessageShown = true;

            const currentOrder = activeOrders.find(o => o._id === orderId);
            if (!currentOrder) {
                openMessagePopup("Order Not Found", "The selected order does not exist.", "error");
                return;
            }

            const rejectable = ["confirmed", "processing"];
            if (!rejectable.includes(currentOrder.status)) {
                openMessagePopup(
                    "Invalid Operation",
                    `Order cannot be rejected from ${currentOrder.status}.`,
                    "error"
                );
                return;
            }

            const response = await axiosInstance.post(
                `/api/orders/${orderId}/reject`,
                {
                    rejectionReason: "Rejected by delivery agent",
                    deliveryPersonName: user?.fullName || "Unknown Delivery Agent",
                    deliveryPersonId: user?._id,
                    rejectedAt: new Date().toISOString()
                },
                { timeout: 10000 }
            );

            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to reject order");
            }

            setActiveOrders(prev => prev.filter(o => o._id !== orderId));

            setStats(prev => ({
                ...prev,
                pendingOrders: Math.max(0, prev.pendingOrders - 1)
            }));

            setPendingNotifications(prev =>
                prev.filter(n => n._id !== orderId)
            );
            setNotificationCount(prev => Math.max(0, prev - 1));

            if (socketRef.current?.connected) {
                socketRef.current.emit("order:rejected", {
                    orderId,
                    previousDeliveryPersonId: currentOrder.deliveryPersonId,
                    status: "pending",
                    rejectionReason: "Rejected by delivery agent",
                    rejectedBy: user?.fullName || "Delivery Agent",
                    rejectedAt: new Date().toISOString(),
                    timestamp: new Date().toISOString()
                });
            }

            openMessagePopup(
                "Order Rejected",
                "Order rejected successfully! It will now be available for other delivery agents.",
                "success"
            );

        } catch (error) {
            console.error("❌ Reject Order Error:", error);

            let errorTitle = "Order Rejection Failed";
            let errorMessage = "Something went wrong.";

            if (error.response) {
                const { status, data } = error.response;

                if (status === 401 || status === 403) {
                    errorTitle = "Session Expired";
                    errorMessage = "Please login again.";
                    setTimeout(() => {
                        logout();
                        navigate("/login");
                    }, 1500);
                } else if (status === 404) {
                    errorTitle = "Order Not Found";
                    errorMessage = "This order no longer exists.";
                    setActiveOrders(prev => prev.filter(o => o._id !== orderId));
                } else if (status === 409) {
                    errorTitle = "Status Changed";
                    errorMessage = "Order status updated. Refreshing...";
                    setTimeout(() => fetchActiveOrders(), 1000);
                } else if (data?.message) {
                    errorMessage = data.message;
                }
            } else if (error.request) {
                errorTitle = "Network Issue";
                errorMessage = "Check your internet connection.";
            } else {
                errorMessage = error.message;
            }

            openMessagePopup(errorTitle, errorMessage, "error");
        }
    };

    const handleAcceptOrder = async (orderId) => {
        try {
            const response = await axiosInstance.put(
                `/api/orders/${orderId}`,
                {
                    status: 'shipped',
                    deliveryPersonId: user._id
                }
            );

            if (response.data) {
                setActiveOrders(prevOrders =>
                    prevOrders.map(order =>
                        order._id === orderId
                            ? { ...order, status: 'shipped' }
                            : order
                    )
                );

                setStats(prev => ({
                    ...prev,
                    pendingOrders: Math.max(0, prev.pendingOrders - 1)
                }));

                if (socketRef.current?.connected) {
                    socketRef.current.emit('order:status-update', {
                        orderId,
                        status: 'shipped',
                        deliveryPersonId: user._id
                    });
                }

                setNotificationCount(prev => Math.max(0, prev - 1));

                toast.success('Order picked up successfully! 📦', {
                    position: 'bottom-right',
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('❌ Error accepting order:', error);
            toast.error('Failed to accept order. Please try again.', {
                position: 'bottom-right',
                autoClose: 4000
            });
        }
    };

    const handleMarkDelivered = async (orderId) => {
        const order = activeOrders.find(o => o._id === orderId);
        if (order && window.confirm(`Mark order #${order._id.slice(-6).toUpperCase()} as delivered?`)) {
            try {
                const response = await axiosInstance.put(
                    `/api/orders/${orderId}`,
                    {
                        status: 'delivered',
                        deliveryPersonId: user._id,
                        deliveredAt: new Date()
                    }
                );

                if (response.data) {
                    setActiveOrders(prevOrders =>
                        prevOrders.filter(o => o._id !== orderId)
                    );

                    const orderAmount = parseFloat(order.amount) || 0;
                    setStats(prev => ({
                        ...prev,
                        completedToday: prev.completedToday + 1,
                        totalEarnings: prev.totalEarnings + orderAmount,
                        todayDeliveries: prev.todayDeliveries + 1
                    }));

                    if (socketRef.current?.connected) {
                        socketRef.current.emit('order:status-update', {
                            orderId,
                            status: 'delivered',
                            deliveryPersonId: user._id
                        });
                    }

                    fetchRecentDeliveries();
                    fetchDeliveryStats();

                    toast.success('Order marked as delivered! ✅', {
                        position: 'bottom-right',
                        autoClose: 3000
                    });
                }
            } catch (error) {
                console.error('❌ Error marking order as delivered:', error);
                toast.error('Failed to mark order as delivered. Please try again.', {
                    position: 'bottom-right',
                    autoClose: 4000
                });
            }
        }
    };

    const handleNavigate = (order) => {
        if (!currentLocation) {
            toast.warning('⚠️ Location not available. Please enable location tracking first.', {
                position: 'bottom-right',
                autoClose: 3000
            });
            return;
        }

        const deliveryAddress = order.address || 'Delivery Address';
        const origin = `${currentLocation.lat},${currentLocation.lng}`;
        const destinationEncoded = encodeURIComponent(deliveryAddress);

        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destinationEncoded}&travelmode=driving`;

        window.open(mapsUrl, '_blank');
    };

    const handleViewCurrentLocation = () => {
        if (!currentLocation) {
            toast.warning('⚠️ Location not available. Please enable location tracking first.', {
                position: 'bottom-right',
                autoClose: 3000
            });
            return;
        }

        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${currentLocation.lat},${currentLocation.lng}`;
        window.open(mapsUrl, '_blank');
    };

    const handleViewOrderDetails = (order) => {
        setSelectedOrder(order);
        setShowOrderModal(true);
    };

    const closeOrderModal = () => {
        setShowOrderModal(false);
        setSelectedOrder(null);
    };

    const handleNotificationClick = () => {
        setShowNotificationDropdown(!showNotificationDropdown);
    };

    // ==================== HELPER FUNCTIONS ====================

    const formatOrderForDisplay = (order) => {
        return {
            id: `#ORD-${order._id?.slice(-6).toUpperCase()}`,
            _id: order._id,
            customer: order.name || 'Customer',
            phone: order.phoneNumber || 'N/A',
            delivery: order.address || 'Delivery Address',
            amount: parseFloat(order.amount) || 0,
            status: order.status,
            items: order.products?.length || 0
        };
    };

    const getUserFirstName = () => {
        if (!user?.fullName) return 'Delivery Partner';
        return user.fullName.split(' ')[0];
    };

    const getProfileImage = () => {
        if (user?.profileImage) {
            if (typeof user.profileImage === 'object' && user.profileImage.url) {
                return user.profileImage.url;
            }
            if (typeof user.profileImage === 'string' && user.profileImage.startsWith('http')) {
                return user.profileImage;
            }
            return `${API_BASE_URL}${user.profileImage}`;
        }
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || 'User')}&background=007b68&color=fff&size=150`;
    };

    const getTimeAgo = (date) => {
        if (!date) return 'Recently';
        const now = new Date();
        const completedDate = new Date(date);
        const diffInMinutes = Math.floor((now - completedDate) / 60000);

        if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours} hours ago`;
        return `${Math.floor(diffInHours / 24)} days ago`;
    };

    const getStatusDisplay = (status) => {
        const statusMap = {
            'pending': { emoji: '⏳', text: 'Pending' },
            'confirmed': { emoji: '🔔', text: 'New Order' },
            'processing': { emoji: '📦', text: 'Processing' },
            'shipped': { emoji: '🚚', text: 'In Transit' },
            'delivered': { emoji: '✅', text: 'Delivered' }
        };
        return statusMap[status] || { emoji: '📦', text: status };
    };

    // ==================== LOADING STATE ====================

    if (!user) {
        return (
            <div className="delivery-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <Loader className="spinner" size={48} />
                    <h2>Loading Dashboard...</h2>
                    <p>Please wait while we load your delivery dashboard</p>
                </div>
            </div>
        );
    }

    // ==================== RENDER ====================

    return (
        <div className="delivery-dashboard">
            {/* ==================== HEADER ==================== */}
            <header className="dashboard-header">
                <div className="header-left">
                    <div className="profile-section">
                        <div className="profile-avatar" onClick={handleViewProfile} style={{ cursor: 'pointer' }}>
                            <img
                                src={getProfileImage()}
                                alt="Profile"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || 'User')}&background=007b68&color=fff&size=150`;
                                }}
                            />
                            <span className={`status-indicator ${isOnline ? 'online' : 'offline'}`}></span>
                        </div>
                        <div className="profile-info">
                            <h2>Welcome Back, {getUserFirstName()}! 👋</h2>
                            <p>
                                {user?.vehicleType && `${user.vehicleType} • `}
                                {user?.email}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="header-right">
                    {/* ONLINE / OFFLINE SWITCH */}
                    <div className="toggle-container">
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={isOnline}
                                onChange={handleStatusToggle}
                                disabled={loading}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span className="toggle-label">
                            {loading ? 'Updating...' : (isOnline ? 'Online' : 'Offline')}
                        </span>
                    </div>

                    {/* GPS SWITCH */}
                    <div className="toggle-container">
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={locationEnabled}
                                onChange={handleLocationToggle}
                                disabled={locationLoading}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span className="toggle-label">
                            {locationLoading ? 'Loading...' : (locationEnabled ? 'GPS On' : 'GPS Off')}
                        </span>
                    </div>

                    {socketConnected && (
                        <div className="live-badge">
                            <span className="pulse-dot"></span> Live Tracking
                        </div>
                    )}
                </div>
            </header>

            {/* ==================== LOCATION STATUS BAR ==================== */}
            {locationEnabled && currentLocation && (
                <div className="location-status-bar" onClick={handleViewCurrentLocation} style={{ cursor: 'pointer' }}>
                    <MapPin size={16} className="pulse-icon" />
                    <span>
                        Live Location Active - {currentAddress || 'Loading address...'}
                        {currentLocation.accuracy && ` (±${currentLocation.accuracy.toFixed(0)}m)`}
                        {socketConnected && ' • Broadcasting via Socket.IO ✅'}
                    </span>
                    <span className="view-on-map">Click to view on map →</span>
                </div>
            )}

            {/* ==================== LOCATION ERROR BAR ==================== */}
            {locationError && (
                <div className="location-error-bar">
                    <AlertCircle size={16} />
                    <span>{locationError}</span>
                    <button className="retry-btn" onClick={retryLocation}>
                        Retry
                    </button>
                </div>
            )}

            {/* ==================== STATS GRID ==================== */}
            <div className="stats-grid">
                <div className="stat-card primary">
                    <div className="stat-icon">
                        <Package size={28} />
                    </div>
                    <div className="stat-content">
                        <p className="stat-label">Total Deliveries</p>
                        <h3 className="stat-value">{stats.todayDeliveries}</h3>
                        <span className="stat-change positive">
                            <TrendingUp size={14} /> {stats.completedToday} completed today
                        </span>
                    </div>
                </div>

                <div
                    className="stat-card success"
                    onClick={() => navigate("/earnings")}
                    style={{ cursor: "pointer", transition: "transform 0.2s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-5px)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                    <div className="stat-icon">
                        <DollarSign size={28} />
                    </div>
                    <div className="stat-content">
                        <p className="stat-label">Total Earnings</p>
                        <h3 className="stat-value">₹{stats.totalEarnings.toFixed(2)}</h3>
                        <span className="stat-change positive">
                            <TrendingUp size={14} /> Great work!
                        </span>
                    </div>
                </div>

                <div
                    className="stat-card warning"
                    onClick={() => navigate('/history')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="stat-icon">
                        <Clock size={28} />
                    </div>
                    <div className="stat-content">
                        <p className="stat-label">Pending Delivery</p>
                        <h3 className="stat-value">{stats.pendingOrders}</h3>
                        <span className="stat-change neutral">
                            {isOnline ? 'View orders →' : 'Go online'}
                        </span>
                    </div>
                </div>

                <div className="stat-card info">
                    <div className="stat-icon">
                        <Star size={28} />
                    </div>
                    <div className="stat-content">
                        <p className="stat-label">Average Rating</p>
                        <h3 className="stat-value">{stats.averageRating.toFixed(1)}</h3>
                        <span className="stat-change positive">
                            {stats.averageRating >= 4.5 ? (
                                <>Excellent! 🌟</>
                            ) : stats.averageRating > 0 ? (
                                <>{stats.averageRating.toFixed(1)}/5.0 stars</>
                            ) : (
                                <>No ratings yet</>
                            )}
                        </span>
                    </div>
                </div>
            </div>

           
            {/* ==================== MAIN CONTENT ==================== */}
            <div className="dashboard-content">
                {/* ==================== ACTIVE ORDERS SECTION ==================== */}
                <div className="content-section active-orders-section xyz">
                    <div className="section-header">
                        <h3>Active Orders</h3>
                        <span className="order-count">{activeOrders.length} Orders</span>
                    </div>
                </div>

                {/* ==================== SIDEBAR SECTION ==================== */}
                <div className="sidebar-section">
                    {/* ==================== RECENT DELIVERIES ==================== */}
                    <div className="content-section recent-deliveries">
                        <div className="section-header">
                            <h3>Recent Deliveries</h3>
                        </div>
                        <div className="recent-list">
                            {recentDeliveries.length > 0 ? (
                                recentDeliveries.map((delivery) => (
                                    <div key={delivery._id} className="recent-item">
                                        <div className="recent-info">
                                            <span className="recent-id">
                                                #ORD-{delivery._id?.slice(-6).toUpperCase()}
                                            </span>
                                            <span className="recent-time">
                                                {getTimeAgo(delivery.deliveredAt || delivery.updatedAt)}
                                            </span>
                                        </div>
                                        <div className="recent-meta">
                                            <span className="recent-amount">₹{parseFloat(delivery.amount || 0).toFixed(2)}</span>
                                            {delivery.deliveryRating && (
                                                <span className="recent-rating">
                                                    <Star size={12} fill="#FFB800" color="#FFB800" />
                                                    {delivery.deliveryRating}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                                    No deliveries yet
                                </p>
                            )}
                        </div>
                    </div>

                    {/* ==================== USER INFO CARD ==================== */}
                    <div className="content-section user-info-card">
                        <div className="section-header">
                            <h3>Your Information</h3>
                        </div>
                        <div className="user-details">
                            <div className="user-detail-item">
                                <span className="detail-label">Phone:</span>
                                <span className="detail-value">{user?.phone || 'N/A'}</span>
                            </div>
                            <div className="user-detail-item">
                                <span className="detail-label">Vehicle:</span>
                                <span className="detail-value">{user?.vehicleType || 'N/A'}</span>
                            </div>
                            {user?.vehicleNumber && (
                                <div className="user-detail-item">
                                    <span className="detail-label">Vehicle No:</span>
                                    <span className="detail-value">{user.vehicleNumber}</span>
                                </div>
                            )}
                            <button
                                className="btn-outline"
                                onClick={handleViewProfile}
                                style={{ marginTop: '15px', width: '100%' }}
                            >
                                View Profile
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== ORDER DETAILS MODAL ==================== */}
            {showOrderModal && selectedOrder && (
                <div className="modal-overlay" onClick={closeOrderModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Order Details</h2>
                            <button className="close-btn" onClick={closeOrderModal}>
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="order-detail-section">
                                <h3>Order ID: #{selectedOrder._id?.slice(-6).toUpperCase()}</h3>
                                <p><strong>Customer:</strong> {selectedOrder.name || 'N/A'}</p>
                                <p><strong>Phone:</strong> {selectedOrder.phoneNumber || 'N/A'}</p>
                                <p><strong>Address:</strong> {selectedOrder.address || 'N/A'}</p>
                                <p><strong>Pincode:</strong> {selectedOrder.pincode || 'N/A'}</p>
                                <p><strong>Amount:</strong> ₹{parseFloat(selectedOrder.amount || 0).toFixed(2)}</p>
                                <p><strong>Status:</strong> {getStatusDisplay(selectedOrder.status).text}</p>
                                <p><strong>Payment Method:</strong> {selectedOrder.paymentMethod || 'N/A'}</p>
                                {selectedOrder.products && selectedOrder.products.length > 0 && (
                                    <div>
                                        <h4>Items Ordered:</h4>
                                        <ul style={{ paddingLeft: '20px' }}>
                                            {selectedOrder.products.map((product, index) => (
                                                <li key={index}>
                                                    {product.productTitle || 'Item'} x {product.quantity || 1} - ₹{parseFloat(product.subTotal || 0).toFixed(2)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* ==================== POPUPS ==================== */}
            {confirmPopup.show && (
                <div className="popup-overlay">
                    <div className="popup-card">
                        <h3 className="popup-title">{confirmPopup.title}</h3>
                        <p className="popup-message">{confirmPopup.message}</p>

                        <div className="popup-buttons">
                            <button
                                className="popup-btn cancel"
                                onClick={closeConfirmPopup}
                            >
                                Cancel
                            </button>

                            <button
                                className="popup-btn ok"
                                onClick={() => {
                                    confirmPopup.onConfirm?.();
                                    closeConfirmPopup();
                                }}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {messagePopup.show && (
                <div className="popup-overlay">
                    <div className="popup-card">
                        <h3 className="popup-title">{messagePopup.title}</h3>
                        <p className="popup-message">{messagePopup.message}</p>

                        <div className="popup-buttons">
                            <button
                                className="popup-btn ok"
                                onClick={closeMessagePopup}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DeliveryDashboard;
